import { useEffect, useState } from 'react';

/**
 * Tiny inline numeric editor used in the inbox labor / equipment tables.
 *
 * Stores its own draft string so the user can type "8." or "" mid-edit
 * without the parent collapsing the state. On blur, parses the draft to
 * a number and pushes upstream via `onChange`. Invalid drafts (NaN, empty)
 * revert to the last good value on blur — no error toast needed, the
 * displayed value just snaps back.
 *
 * `decimals` controls both the on-blur normalization and the displayed
 * precision. Money uses 2, hours uses 1.
 */
export interface InlineNumberInputProps {
  value: number;
  onChange: (v: number) => void;
  decimals: 0 | 1 | 2;
  disabled?: boolean;
  ariaLabel?: string;
  /** Right-align the input — table cells with numbers want this. */
  align?: 'left' | 'right';
  /** Tweakable max width; defaults to 80px (fits 4 digits + decimal comfortably). */
  width?: number;
}

export function InlineNumberInput({
  value,
  onChange,
  decimals,
  disabled = false,
  ariaLabel,
  align = 'right',
  width = 80,
}: InlineNumberInputProps) {
  // Local draft so partial input ("8.", "") doesn't lose the user's place.
  const [draft, setDraft] = useState<string>(() => formatForDisplay(value, decimals));

  // Sync when the upstream value changes for non-edit reasons (parent reset).
  useEffect(() => {
    setDraft(formatForDisplay(value, decimals));
  }, [value, decimals]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const parsed = parseDraft(draft);
        if (parsed === null) {
          setDraft(formatForDisplay(value, decimals));
          return;
        }
        const rounded = Math.round(parsed * 10 ** decimals) / 10 ** decimals;
        if (rounded !== value) onChange(rounded);
        setDraft(formatForDisplay(rounded, decimals));
      }}
      onFocus={(e) => e.currentTarget.select()}
      aria-label={ariaLabel}
      disabled={disabled}
      style={{
        width,
        padding: '4px 6px',
        border: '1px solid var(--rule)',
        borderRadius: 3,
        background: disabled ? 'var(--surface)' : '#fff',
        fontFamily: 'var(--mono)',
        fontSize: 13,
        textAlign: align,
        color: 'var(--ink)',
      }}
    />
  );
}

function parseDraft(s: string): number | null {
  const cleaned = s.trim().replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;
  return n;
}

function formatForDisplay(value: number, decimals: 0 | 1 | 2): string {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(decimals);
}
